import { useCallback, useMemo, useState } from "react";

import moment from "moment";
import { useToasts } from "react-toast-notifications";

import { LoadingSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { forecastToBudgetMix } from "@features/mmm/utils/budget-mix-forecast-input-helpers";
import { FETCH_FORECAST } from "@gql/mmm-scenario-modeling";
import { useNorthbeamMutationPromise } from "@hooks/http";
import {
  type Forecast_me_mmm_forecast_prediction,
  type MMMForecastDataInput,
  type Forecast,
  type ForecastVariables,
  type CreateCustomBudgetMix,
  type CreateCustomBudgetMixVariables,
} from "@north-beam/nb-client-side";
import { type FullBudgetMixResponse } from "@north-beam/nb-common";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";
import { Modal } from "@shared/modal";

import { useClientKpis } from "../../../../pages/mmm/hooks/use-client-kpis";
import { CREATE_CUSTOM_BUDGET_MIX } from "../explore-budgets/adopt-mix-mutation";
import { useBudgetMix } from "../explore-budgets/budget-mix-card/use-budget-mix";
import { type BudgetMixInput } from "../explore-budgets/scenario-modeling/types";
import { usePerformanceTrackingKpiData } from "../performance-tracking/hooks/use-performance-tracking-kpi-data";

import { UpdateBudgetPacingAllocation } from "./buget-pacing-table";
import { CurrentForecastHybridChart } from "./forecast-comparison/current-forecast-hybrid-chart";
import { ForecastComparisonWidget } from "./forecast-comparison/forecast-comparison-widget";
import { RebudgetingTableProvider } from "./hooks/use-table-store";

export function BudgetPacingModal({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}) {
  const { user } = useUser();
  const { addToast } = useToasts();
  const { adoptedBudgetMix, adoptedKpiName } = useAdoptedBudgetMix();
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [errorLoadingForecast, setErrorLoadingForecast] = useState<
    string | Error | null
  >(null);

  const { selectedCycle } = useSelectedCycle();

  const { adoptBudgetMix } = useBudgetMix();

  const { trackingKpiData, loading: loadingTrackingKpiData } =
    usePerformanceTrackingKpiData();

  const { kpis } = useClientKpis();
  const selectedKpi = useMemo(() => {
    const selected = kpis.find((i) => i.kpi_name === adoptedKpiName);
    if (!selected) {
      return kpis[0];
    }
    return selected;
  }, [adoptedKpiName, kpis]);

  const [loadingAdoption, setLoadingAdoption] = useState(false);

  const [forecastPrediction, setForecastPrediction] = useState<
    Forecast_me_mmm_forecast_prediction[] | null
  >(null);
  const [forecastSpendData, setForecastSpendData] = useState<
    MMMForecastDataInput[] | null
  >(null);

  const [proposedBudgetMix, setProposedBudgetMix] =
    useState<BudgetMixInput | null>(null);

  const [createCustomBudgetMix] = useNorthbeamMutationPromise<
    CreateCustomBudgetMix,
    CreateCustomBudgetMixVariables
  >(CREATE_CUSTOM_BUDGET_MIX);

  const handleAdoptBudgetMix = useCallback(async () => {
    if (proposedBudgetMix) {
      setLoadingAdoption(true);
      try {
        const createdBudgetMixResponse = await createCustomBudgetMix({
          cycleId: selectedCycle.id,
          data: proposedBudgetMix,
          name: `Rebalanced Budget ${moment().format("MM/DD/YYYY h:mm A")}`,
        });
        const createdBudgetMixId =
          createdBudgetMixResponse.createCustomBudgetMix.budgetMix.id;
        await adoptBudgetMix({
          id: createdBudgetMixId,
        });
        addToast("Successfully updated your adopted budget", {
          appearance: "success",
        });
        setIsOpen(false);
      } catch (err) {
        addToast("There was an error adopting your custom budget", {
          appearance: "error",
        });
      } finally {
        setLoadingAdoption(false);
      }
    }
  }, [
    adoptBudgetMix,
    createCustomBudgetMix,
    addToast,
    proposedBudgetMix,
    selectedCycle.id,
    setIsOpen,
  ]);

  // NOTE: Using user.apolloClient because I want the load
  // call to be async so that the loading button (a few descendents
  // down) can properly render a loading state after press.
  // useNorthbeamLazyQuery provides a `loading` state and a load function
  // that is not async. I could pass both down as props but am not a fan of that.

  // POTENTIAL REFACTOR NOTE: Consider putting into a hook to avoid prop drilling,
  // would also prevent issue described above. Not currently worth creating
  // global state & new provider IMO as it is used only in one component.
  const getForecast = useCallback(
    async (forecastInput: ForecastVariables) => {
      try {
        setLoadingForecast(true);
        setForecastSpendData(forecastInput.input.data);
        const res = await user.apolloClient.query<Forecast, ForecastVariables>({
          query: FETCH_FORECAST,
          variables: forecastInput,
        });
        setForecastPrediction(res.data.me.mmm.forecast.prediction);
        setProposedBudgetMix(
          forecastToBudgetMix(
            forecastInput.input.data,
            res.data.me.mmm.forecast.prediction,
            kpis,
            selectedKpi,
            selectedCycle.date_range,
            res.data.me.mmm.forecast.forecastedContributionsByKpi,
          ),
        );
        setErrorLoadingForecast(null);
        return res.data.me.mmm.forecast;
      } catch (err) {
        addToast("There was a problem forecasting your changes", {
          appearance: "error",
        });
        if (err instanceof Error) {
          setErrorLoadingForecast(err);
        } else {
          setErrorLoadingForecast(String(err));
        }
      } finally {
        setLoadingForecast(false);
      }
    },
    [user.apolloClient, selectedCycle.date_range, kpis, selectedKpi, addToast],
  );

  const onClose = useCallback(() => setIsOpen(false), [setIsOpen]);

  return (
    <RebudgetingTableProvider>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        headerProps={{ className: "float-right" }}
        bodyClassName="px-0 py-0"
      >
        <div className="flex flex-row max-w-full">
          <div className="flex flex-col bg-interstellar-2">
            <div className="w-[25rem] rounded-sm p-5 bg-interstellar-3 mr-1 mb-1">
              <div className="font-semibold">Current Forecast</div>
              {!loadingAdoption && adoptedBudgetMix ? (
                <CurrentForecastHybridChart
                  selectedKpi={selectedKpi}
                  actualDailyKpiData={trackingKpiData.dailyMetrics}
                  adoptedMixWeeklyValues={adoptedBudgetMix.data.weekly_data}
                  forecastedMixWeeklyValues={
                    loadingForecast ? [] : proposedBudgetMix?.weekly_data
                  }
                  cycleDateRange={selectedCycle.date_range}
                  loading={loadingTrackingKpiData}
                  height={300}
                />
              ) : (
                <div style={{ height: "300px" }}>
                  <LoadingSlide text="Setting new adopted budget..." />
                </div>
              )}
            </div>
            <div className="flex flex-col justify-start w-[25rem] rounded-sm bg-interstellar-3 mr-1 h-full">
              {!forecastPrediction && !loadingForecast ? (
                <div className="h-full flex items-center pt-3 mb-3">
                  <div className="flex rounded-sm h-full text-interstellar-1 text-center items-center  ">
                    Click &quot;Forecast&quot; to see the predictions of your
                    budget
                  </div>
                </div>
              ) : (
                <div className="h-full w-full flex flex-col justify-start gap-4">
                  <ForecastComparisonWidget
                    adoptedBudgetMix={adoptedBudgetMix as FullBudgetMixResponse}
                    proposedBudgetMix={proposedBudgetMix}
                    selectedKpi={selectedKpi}
                    isLoading={loadingForecast}
                    height={244}
                  />
                  {!loadingForecast && (
                    <div
                      id="modal-left-footer"
                      className="h-full bg-interstellar-3 mr-1 w-full"
                    ></div>
                  )}
                </div>
              )}
            </div>
          </div>
          <UpdateBudgetPacingAllocation
            getForecast={getForecast}
            allowForecastAdoption={!!proposedBudgetMix}
            forecastError={!!errorLoadingForecast}
            onAdoptForecast={handleAdoptBudgetMix}
          />
        </div>
      </Modal>
    </RebudgetingTableProvider>
  );
}
